import { CalendarPlus, DotsSixVertical, X } from "@phosphor-icons/react";
import { m } from "@plannello/i18n/messages";
import type { ItemLite } from "@plannello/shared";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useRef } from "react";
import { ItemKey } from "@/components/domain/ItemKey";
import { PriorityIcon } from "@/components/domain/PriorityIcon";
import { StatusIcon } from "@/components/domain/StatusIcon";
import { IconButton } from "@/components/ui";
import type { Catalog } from "@/data/catalog";
import { TranslatedMark, titleTooltip, useTitles } from "@/data/titles";
import { loc } from "@/lib/format";
import { UNSCHEDULED_DRAG_TYPE } from "./TimelineCanvas";

/**
 * Items of the view without start or due date, next to the timeline. Drag one onto the canvas to
 * give it a week starting where you drop it, or press its calendar button (Set dates) for the
 * keyboard path. Virtualised: a product with thousands of undated items stays smooth.
 */
export function UnscheduledPanel({
  items,
  catalog,
  onSetDates,
  onPeek,
  onClose,
}: {
  items: ItemLite[];
  catalog: Catalog;
  onSetDates: (item: ItemLite, anchor: HTMLElement) => void;
  onPeek: (item: ItemLite) => void;
  onClose: () => void;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const titleOf = useTitles();
  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => 52,
    overscan: 8,
    getItemKey: (i) => items[i]!.id,
  });
  return (
    <aside
      aria-labelledby="tl-unscheduled"
      className="flex w-72 shrink-0 flex-col border-l border-line bg-subtle max-lg:w-60"
    >
      <div className="flex h-[52px] shrink-0 items-center gap-2 border-b border-line pr-1.5 pl-3">
        <h2 id="tl-unscheduled" className="text-sm font-semibold text-fg">
          {m.tl_unscheduled()}
        </h2>
        <span className="text-xs text-fg-subtle tnum">{items.length}</span>
        <span className="flex-1" />
        <IconButton aria-label={m.tl_unscheduled_hide()} icon={X} size="sm" onPress={onClose} />
      </div>
      {items.length === 0 ? (
        <p className="p-3 text-xs text-pretty text-fg-muted">{m.tl_unscheduled_empty()}</p>
      ) : (
        <>
          <p className="shrink-0 px-3 pt-2 pb-1 text-2xs text-pretty text-fg-subtle">{m.tl_unscheduled_hint()}</p>
          <div ref={scrollRef} className="scroll-thin min-h-0 flex-1 overflow-y-auto px-1.5 pb-3">
            <ul aria-labelledby="tl-unscheduled" className="relative" style={{ height: virtualizer.getTotalSize() }}>
              {virtualizer.getVirtualItems().map((v) => {
                const item = items[v.index]!;
                const status = catalog.statusById.get(item.statusId);
                const shown = titleOf(item);
                return (
                  <li
                    key={v.key}
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(UNSCHEDULED_DRAG_TYPE, item.id);
                      e.dataTransfer.setData("text/plain", `${item.key} ${shown.text}`);
                      e.dataTransfer.effectAllowed = "move";
                    }}
                    className="group/u absolute inset-x-0 top-0 flex cursor-grab items-start gap-1.5 rounded-control px-1.5 py-1.5 hover:bg-hover active:cursor-grabbing"
                    style={{ transform: `translateY(${v.start}px)`, height: 52 }}
                  >
                    <DotsSixVertical
                      size={14}
                      aria-hidden="true"
                      className="mt-0.5 shrink-0 text-fg-subtle opacity-60"
                    />
                    <button
                      type="button"
                      onClick={() => onPeek(item)}
                      className="flex min-w-0 flex-1 flex-col items-start gap-0.5 rounded-mark text-left outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus"
                    >
                      <span className="flex items-center gap-1.5">
                        <PriorityIcon value={item.priority} size={12} />
                        <ItemKey value={item.key} />
                        {status ? <StatusIcon category={status.category} label={loc(status.name)} size={12} /> : null}
                      </span>
                      <span className="w-full truncate-title text-xs text-fg" title={titleTooltip(shown, item.title)}>
                        {shown.text}
                        <TranslatedMark d={shown} />
                      </span>
                    </button>
                    <IconButton
                      aria-label={m.tl_set_dates_for({ key: item.key })}
                      icon={CalendarPlus}
                      size="sm"
                      onPress={(e) => onSetDates(item, e.target as HTMLElement)}
                      className="opacity-60 group-hover/u:opacity-100 focus-visible:opacity-100"
                    />
                  </li>
                );
              })}
            </ul>
          </div>
        </>
      )}
    </aside>
  );
}
